import { fileRecordKeys } from "@/api/fileRecords";
import { isFileTypeAllowed } from "@/dnd/osFileDrop";
import {
  useFileRecordUpload,
  type UploadTarget,
} from "@/hooks/useFileRecordUpload";
import { flattenDropPayloadFiles } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/RailOsFileDropZone";
import type { WorkspaceDropPayload } from "@/pages/workspaces/components/WorkspaceDropZone";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import toast from "react-hot-toast";

/**
 * OS-file drop handler for a rail canvas/chat node: uploads the dropped files
 * into the node's scope (flat — folder structure has no meaning there) through
 * the standard upload pipeline (conflict resolver + S3 queue), then refreshes
 * the scope's file list so the new members appear under the expanded node.
 */
export function useRailScopeOsDrop(
  scope: { canvasId: string } | { flowSessionId: string },
): (payload: WorkspaceDropPayload) => void {
  const { enqueueFiles } = useFileRecordUpload();
  const queryClient = useQueryClient();

  const scopeId = "canvasId" in scope ? scope.canvasId : scope.flowSessionId;
  const isCanvas = "canvasId" in scope;

  const { target, listKey } = useMemo(
    () =>
      isCanvas
        ? {
            target: { class: "canvas", canvas_id: scopeId } as UploadTarget,
            listKey: fileRecordKeys.canvas(scopeId),
          }
        : {
            target: {
              class: "flow-session",
              flow_session_id: scopeId,
            } as UploadTarget,
            listKey: fileRecordKeys.flowSession(scopeId),
          },
    [isCanvas, scopeId],
  );

  return useCallback(
    (payload: WorkspaceDropPayload) => {
      const all = flattenDropPayloadFiles(payload);
      const files = all.filter(isFileTypeAllowed);
      for (const rejected of all.filter((f) => !isFileTypeAllowed(f))) {
        toast.error(`Unsupported file type: ${rejected.name}`);
      }
      if (files.length === 0) return;

      void enqueueFiles(files, target)
        .catch(() => toast.error("Failed to upload files"))
        .finally(() => {
          void queryClient.invalidateQueries({ queryKey: listKey });
        });
    },
    [enqueueFiles, queryClient, target, listKey],
  );
}
